import React, {useEffect, useState} from 'react'
import style from './style.less'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import CircularProgress from '@mui/material/CircularProgress'
import Typography, {TypographySizes, TypographyTypes,} from '../Typography/Typography'
import Button from '../Button/Button'
import Switch from '../Switch'
import classNames from 'classnames'
import {FormListInput} from "./FormListInput";
import {FormTextInput, FormTextInputProps} from "./FormTextInput";


export const FormDialogTypes = {
  edit: 'edit',
  creating: 'creating',
}

export interface SelectionProp {
  value: any
  name: string
  item: React.ReactNode | ((props: any) => React.ReactElement)
}

export interface FormInputProps {
  name: string
  title: string
  description?: string
  placeHolderText?: string
  required?: boolean
  disabled?: boolean
}


export interface FormListInputProps extends FormInputProps {
  value?: string | null | string[]
  selections: Array<SelectionProp>
  editable?: boolean
  multiple?: boolean
  className?: string
  inputProps?: {
    style?: React.CSSProperties
  }
}

export interface FormBooleanInputProps extends FormInputProps {
  value?: boolean
}

export interface FormCustomInputProps extends FormInputProps {
  renderInput: (onChange: (name: string, value: any) => void, defaultValue?: any) => React.ReactElement
  value?: any
}

export type InputProp =
  | FormTextInputProps
  | FormListInputProps
  | FormBooleanInputProps
  | FormCustomInputProps

export interface FormDetailsEntry {
  icon: React.ReactNode
  value: React.ReactNode
}

export interface FormDialogProps {
  title: string
  titleIcon: React.ReactNode
  open: boolean
  dialogType?: string
  inputs: () => Promise<Array<InputProp>>
  confirmationTitle: string
  toDetailEntries?: (result: any) => Array<FormDetailsEntry>
  onCancel: () => void
  onConfirmed: (result: any) => Promise<boolean | void>
  onDelete?: () => void
  buttonTexts?: {
    cancel?: string
    submit?: string
    saveChanges?: string
    editDetails?: string
    delete?: string
  }
}

const FormDialog: React.FunctionComponent<FormDialogProps> = ({
  title,
  titleIcon,
  open,
  inputs,
  confirmationTitle,
  dialogType = FormDialogTypes.creating,
  toDetailEntries,
  onCancel,
  onConfirmed,
  onDelete,
  buttonTexts = {},
}) => {
  const [resolvedInputs, setResolvedInputs] = useState<Array<InputProp>>([])
  const [result, setResult] = useState<any>({})
  const [ready, setReady] = useState<boolean>(false)
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [defaultValues, setDefaultValues] = useState<any>({})
  const [requiredFields, setRequiredFields] = useState<Array<string>>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)

  useEffect(() => {
    setRequiredFields(
      resolvedInputs
        .filter((input) => input.required)
        .map((input) => input.name)
    )
    setResult(
      resolvedInputs.reduce(
        (pre, curr) =>
          curr.value ? Object.assign(pre, { [curr.name]: curr.value }) : pre,
        {}
      )
    )
    setDefaultValues(
      resolvedInputs.reduce(
        (pre, curr) =>
          curr.value ? Object.assign(pre, { [curr.name]: curr.value }) : pre,
        {}
      )
    )
  }, [resolvedInputs])

  useEffect(() => {
    if (!open) {
      resetDialog()
      return
    }
    setLoading(true)
    inputs()
      .then((resolved) => {
        setResolvedInputs(resolved)
      })
      .catch(() => onCancel())
      .finally(() => setLoading(false))
  }, [open])

  const isReadyToSubmit = () =>
    requiredFields.every((field) => result[field] !== undefined) &&
    resolvedInputs
      .filter(
        (i): i is FormTextInputProps =>
          (i as FormTextInputProps).validate !== undefined
      )
      .every(({ name, validate }) =>
        result[name] !== undefined || result[name] !== null
          ? validate!(result[name])
          : true
      )

  const onFieldChanged = (fieldName: string, value: any) => {
    result[fieldName] = value
    setResult(result)
    setReady(isReadyToSubmit())
  }

  const onDialogConfirm = async () => {
    setSubmitting(true)
    try {
      const success = await onConfirmed(result)
      if (success === false) {
        setSubmitted(false)
      }
    } catch {
      setSubmitted(false)
    } finally {
      setSubmitting(false)
    }
  }

  const onDialogCancel = () => {
    if (submitting) return
    onCancel()
  }

  const resetDialog = () => {
    setResult({})
    setDefaultValues({})
    setReady(false)
    setSubmitted(false)
    setLoading(false)
    setSubmitting(false)
  }

  const editDialog = (result: any) => {
    return (
      <>
        <Typography type={TypographyTypes.title} size={TypographySizes.large}>
          {title}
        </Typography>
        <Typography type={TypographyTypes.label}>
          <div className={style.inputBox}>
            {resolvedInputs.map(toInputField(result))}
          </div>
        </Typography>
      </>
    )
  }

  const toInputField = (result: any) => {
    const input = (input: InputProp, idx: number) => {
      let inputField
      if ('renderInput' in input) {
        inputField = (input as FormCustomInputProps).renderInput(
          onFieldChanged,
          result[input.name] ?? defaultValues[input.name]
        )
      } else if ('value' in input && typeof input.value === 'boolean') {
        inputField = (
          <FormBooleanInput
            props={input as FormBooleanInputProps}
            onChange={onFieldChanged}
            defaultValue={result[input.name] ?? input.value ?? false}
          />
        )
      } else {
        const defaultValue =
          result[input.name] || defaultValues[input.name] || ''
        inputField = (
          <div className={style.inputFieldBox}>
            {`${input.title}${input.required ? '*' : ''}`}
            {'selections' in input ? (
              <FormListInput
                props={input as FormListInputProps}
                defaultValue={defaultValue}
                onChange={onFieldChanged}
              />
            ) : (
              <FormTextInput
                props={input as FormTextInputProps}
                onChange={onFieldChanged}
                defaultValue={defaultValue}
              />
            )}
          </div>
        )
      }

      return (
        <div key={`${idx}_${Object.keys(defaultValues).length}`}>
          {' '}
          {inputField}
        </div>
      )
    }
    return input
  }

  const confirmationDialog = () => {
    return (
      <>
        <Typography type={TypographyTypes.title} size={TypographySizes.large}>
          {confirmationTitle}
        </Typography>
        <Typography type={TypographyTypes.body}>
          <div className={style.detailsBox}>
            {toDetailEntries?.(result).map(toDetailsItem)}
          </div>
        </Typography>
      </>
    )
  }

  const toDetailsItem = (input: FormDetailsEntry, idx: number) => (
    <div className={style.detailsItem} key={idx}>
      {input.icon}
      {input.value}
    </div>
  )

  const submitButton = (
    <Button
      onClick={() => setSubmitted(true)}
      text={buttonTexts.submit || 'Submit'}
      disabled={!ready}
    />
  )

  const saveChangesButton = (
    <Button onClick={onDialogConfirm} text={buttonTexts.saveChanges || 'Save changes'} disabled={!ready} />
  )

  return (
    <Dialog
      maxWidth="md"
      fullWidth={true}
      open={open}
      PaperProps={{ sx: { borderRadius: '0.75rem' } }}
      onClose={(_, reason) => {
        if (submitting) return
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          onDialogCancel()
        }
      }}
    >
      <DialogContent className={style.dialog}>
        <div className={style.titleIcon}>{titleIcon}</div>
        {loading
          ? <div className={style.loadingBox}><CircularProgress size={32} /></div>
          : submitted && dialogType === FormDialogTypes.creating
            ? confirmationDialog()
            : editDialog(result)}
        <div className={style.dialogActions}>
          <div className={style.dialogDefaultActionBox}>
            <Button onClick={onDialogCancel} text={buttonTexts.cancel || 'Cancel'} secondary disabled={submitting} />
            {submitted ? (
              <Button
                onClick={onDialogConfirm}
                text={submitting ? undefined : title}
                icon={submitting
                  ? <CircularProgress size={20} color="inherit" />
                  : undefined}
                className={style.confirmActionButton}
                disabled={submitting}
              />
            ) : dialogType === FormDialogTypes.creating ? (
              submitButton
            ) : (
              saveChangesButton
            )}
          </div>
          {submitted ? (
            <Button
              onClick={() => !submitting && setSubmitted(false)}
              text={buttonTexts.editDetails || 'Edit details'}
              tertiary
              disabled={submitting}
            />
          ) : undefined}
          {onDelete && (
            <Button
              onClick={onDelete}
              text={buttonTexts.delete || 'Delete'}
              tertiary
              className={style.deleteButton}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

const FormBooleanInput: React.FunctionComponent<{
  props: FormBooleanInputProps
  defaultValue: boolean
  onChange: (fieldName: string, value: any) => void
}> = ({ props, defaultValue, onChange }) => {
  const [state, setState] = useState<boolean>(defaultValue)

  useEffect(() => setState(defaultValue), [defaultValue])

  const onStateChange = (event: any) => {
    event.stopPropagation()
    setState(!state)
    onChange(props.name, !state)
  }

  return (
    <div
      className={classNames(
        style.booleanInput,
        props.disabled ? style.booleanInputDisabled : undefined
      )}
    >
      <Typography type={TypographyTypes.body}>{props.title}</Typography>
      <div className={style.toggle}>
        <Switch
          checked={state}
          onClick={(event) => event.stopPropagation()}
          onChange={onStateChange}
          disabled={props.disabled}
        />
      </div>
    </div>
  )
}

export default FormDialog
